import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  IconButton,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { FilterList, Clear, Download } from '@mui/icons-material';
import { ReportFilters as ReportFiltersType } from '../utils/types';

type ReportFiltersProps = {
  onFiltersChange: (filters: ReportFiltersType) => void;
  onExport: (filters: ReportFiltersType) => void;
  jewelers?: Array<{ id: string; name: string; companyName?: string }>;
  branches?: Array<{ id: string; name: string }>;
  loading?: boolean;
  hideJeweler?: boolean;
};

export default function ReportFilters({ 
  onFiltersChange, 
  onExport, 
  jewelers = [], 
  branches = [], 
  loading = false,
  hideJeweler = false,
}: ReportFiltersProps) {
  const [filters, setFilters] = useState<ReportFiltersType>({
    reportType: 'monthly',
    period: 'monthly',
    periodOffset: 0,
    startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key: keyof ReportFiltersType, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: ReportFiltersType = {
      reportType: 'monthly',
      period: 'monthly',
      periodOffset: 0,
      startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD')
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'monthly': return 'Aylık';
      case 'quarterly': return 'Çeyreklik';
      case 'yearly': return 'Yıllık';
      case 'custom': return 'Özel Tarih';
      default: return type;
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (!hideJeweler && filters.jewelerId) count++;
    if (filters.branchId) count++;
    if (filters.startDate && filters.endDate && filters.reportType === 'custom') count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
      <Box
        sx={{
          background: '#F8F9FA',
          borderRadius: 2,
          p: 3,
          mb: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Rapor Filtreleri
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowAdvanced(!showAdvanced)}
              size="small"
            >
              {showAdvanced ? 'Basit' : 'Gelişmiş'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClearFilters}
              size="small"
            >
              Temizle
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => onExport(filters)}
              disabled={loading}
              size="small"
            >
              Excel İndir
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Rapor Türü</InputLabel>
              <Select
                value={filters.reportType}
                label="Rapor Türü"
                onChange={(e) => {
                  const newReportType = e.target.value;
                  handleFilterChange('reportType', newReportType);
                  if (newReportType !== 'custom') {
                    handleFilterChange('period', newReportType);
                  }
                }}
              >
                <MenuItem value="monthly">Aylık</MenuItem>
                <MenuItem value="quarterly">Çeyreklik</MenuItem>
                <MenuItem value="yearly">Yıllık</MenuItem>
                <MenuItem value="custom">Özel Tarih</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filters.reportType !== 'custom' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Dönem</InputLabel>
                <Select
                  value={filters.periodOffset || 0}
                  label="Dönem"
                  onChange={(e) => handleFilterChange('periodOffset', parseInt(e.target.value as string))}
                >
                  <MenuItem value={0}>Bu {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'}</MenuItem>
                  <MenuItem value={1}>Geçen {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'}</MenuItem>
                  <MenuItem value={2}>2 {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'} Önce</MenuItem>
                  <MenuItem value={3}>3 {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'} Önce</MenuItem>
                  <MenuItem value={6}>6 {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'} Önce</MenuItem>
                  <MenuItem value={12}>12 {filters.reportType === 'monthly' ? 'Ay' : filters.reportType === 'quarterly' ? 'Çeyrek' : 'Yıl'} Önce</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {filters.reportType === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Başlangıç Tarihi"
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  onChange={(date) => handleFilterChange('startDate', date?.format('YYYY-MM-DD'))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Bitiş Tarihi"
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  onChange={(date) => handleFilterChange('endDate', date?.format('YYYY-MM-DD'))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            </>
          )}

          {showAdvanced && (
            <>
              {!hideJeweler && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Kuyumcu</InputLabel>
                    <Select
                      value={filters.jewelerId || ''}
                      label="Kuyumcu"
                      onChange={(e) => handleFilterChange('jewelerId', e.target.value || undefined)}
                    >
                      <MenuItem value="">Tümü</MenuItem>
                      {jewelers.map((jeweler) => (
                        <MenuItem key={jeweler.id} value={jeweler.id}>
                          {jeweler.companyName || `${jeweler.name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {branches.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mağaza</InputLabel>
                    <Select
                      value={filters.branchId || ''}
                      label="Mağaza"
                      onChange={(e) => handleFilterChange('branchId', e.target.value || undefined)}
                    >
                      <MenuItem value="">Tümü</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
        </Grid>

        {getActiveFiltersCount() > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>
              Aktif Filtreler:
            </Typography>
            {!hideJeweler && filters.jewelerId && (
              <Chip
                label={`Kuyumcu: ${jewelers.find(j => j.id === filters.jewelerId)?.companyName || 'Seçili'}`}
                onDelete={() => handleFilterChange('jewelerId', undefined)}
                size="small"
              />
            )}
            {filters.branchId && (
              <Chip
                label={`Mağaza: ${branches.find(b => b.id === filters.branchId)?.name || 'Seçili'}`}
                onDelete={() => handleFilterChange('branchId', undefined)}
                size="small"
              />
            )}
            {filters.reportType === 'custom' && filters.startDate && filters.endDate && (
              <Chip
                label={`Tarih: ${dayjs(filters.startDate).format('DD.MM.YYYY')} - ${dayjs(filters.endDate).format('DD.MM.YYYY')}`}
                onDelete={() => {
                  handleFilterChange('startDate', undefined);
                  handleFilterChange('endDate', undefined);
                }}
                size="small"
              />
            )}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
